import { useRef, useState } from 'react';
import { LESSONS } from '../content/lessons';
import type { LessonStatus } from '../state/progress';
import { useApp } from '../state/store';
import { lessonPath, navigate, type Route } from './router';

/** Append `?courseMenu=1` (see README) to reveal export / import / reset. */
export const COURSE_MENU_PARAM = 'courseMenu';
const COURSE_MENU_FLAG = 'acl-course-menu';

function courseMenuUnlocked(): boolean {
  if (typeof window === 'undefined') return false;
  try {
    if (sessionStorage.getItem(COURSE_MENU_FLAG) === '1') return true;
    if (new URLSearchParams(window.location.search).get(COURSE_MENU_PARAM) === '1') {
      sessionStorage.setItem(COURSE_MENU_FLAG, '1');
      return true;
    }
  } catch {
    // private mode / blocked storage: fall through to live URL check only
    return new URLSearchParams(window.location.search).get(COURSE_MENU_PARAM) === '1';
  }
  return false;
}

export function Header({ statuses, route, sandboxOpen }: { statuses: LessonStatus[]; route: Route; sandboxOpen: boolean }) {
  const passed = statuses.filter((s) => s.gatePassed).length;
  const storageStatus = useApp((s) => s.storageStatus);
  const storageMessage = useApp((s) => s.storageMessage);
  const [menu, setMenu] = useState(false);
  const [showCourse] = useState(courseMenuUnlocked);
  const menuBtn = useRef<HTMLButtonElement>(null);

  return (
    <header className="app-header">
      <a href="#main" className="skip-link">
        Skip to lesson
      </a>
      <div className="brand">
        <a href="/" onClick={(e) => (e.preventDefault(), navigate('/'))}>
          Altitude Control Lab
        </a>
        <span className="muted small">Introduction to Robotics</span>
      </div>
      <nav aria-label="Modules" className="module-nav">
        <ol>
          {statuses.map((s) => {
            const current = route.name === 'lesson' && route.id === s.lesson.id;
            const label = `Module ${s.lesson.number}: ${s.lesson.title}${s.gatePassed ? ' (complete)' : s.unlocked ? '' : ' (locked)'}`;
            return (
              <li key={s.lesson.id}>
                <a
                  href={lessonPath(s.lesson.id)}
                  aria-current={current ? 'page' : undefined}
                  aria-label={label}
                  className={`mod${s.gatePassed ? ' done' : ''}${s.unlocked ? '' : ' locked'}${current ? ' current' : ''}`}
                  onClick={(e) => {
                    e.preventDefault();
                    navigate(lessonPath(s.lesson.id));
                  }}
                >
                  <span className="mod-num" aria-hidden="true">
                    {s.lesson.number}
                  </span>
                  {!s.unlocked && (
                    <span className="mod-badge" aria-hidden="true">
                      🔒
                    </span>
                  )}
                  <span className="mod-tip">{s.lesson.title}</span>
                </a>
              </li>
            );
          })}
          <li>
            <a
              href="/sandbox"
              aria-current={route.name === 'sandbox' ? 'page' : undefined}
              className={`mod sandbox${sandboxOpen ? '' : ' locked'}${route.name === 'sandbox' ? ' current' : ''}`}
              aria-label={`Summary${sandboxOpen ? '' : ' (locked until the capstone is passed)'}`}
              onClick={(e) => {
                e.preventDefault();
                navigate('/sandbox');
              }}
            >
              Summary
            </a>
          </li>
        </ol>
        <span className="small muted progress-count">
          {passed}/{LESSONS.length} modules
        </span>
      </nav>
      <div className="header-actions">
        {(storageStatus === 'failed' || storageStatus === 'unavailable') && (
          <span className="badge warn" role="status" title={storageMessage ?? ''}>
            Progress is not being saved
          </span>
        )}
        <a href="/glossary" className="btn small ghost" onClick={(e) => (e.preventDefault(), navigate('/glossary'))}>
          Glossary
        </a>
        {showCourse && (
          <>
            <button ref={menuBtn} type="button" className="btn small ghost" aria-expanded={menu} aria-controls="course-menu" onClick={() => setMenu(!menu)}>
              Course ▾
            </button>
            {menu && <CourseMenu onClose={() => (setMenu(false), menuBtn.current?.focus())} />}
          </>
        )}
      </div>
    </header>
  );
}

function CourseMenu({ onClose }: { onClose: () => void }) {
  const exportData = useApp((s) => s.exportData);
  const importData = useApp((s) => s.importData);
  const resetCourse = useApp((s) => s.resetCourse);
  const notify = useApp((s) => s.notify);
  const prefs = useApp((s) => s.prefs);
  const setPrefs = useApp((s) => s.setPrefs);
  const storageMessage = useApp((s) => s.storageMessage);
  const fileRef = useRef<HTMLInputElement>(null);
  const [mode, setMode] = useState<'merge' | 'replace'>('merge');
  const [confirmReset, setConfirmReset] = useState('');

  const download = () => {
    const blob = new Blob([exportData()], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `altitude-control-progress-${new Date().toISOString().slice(0, 10)}.json`;
    a.click();
    URL.revokeObjectURL(url);
    notify('success', 'Progress exported to a JSON file.');
  };

  return (
    <div id="course-menu" className="menu" role="dialog" aria-label="Course menu" onKeyDown={(e) => e.key === 'Escape' && onClose()}>
      <section>
        <h3>Progress</h3>
        <p className="small">
          Progress is stored only in this browser, for this site address. It does not move to another browser, device, or between the local and
          deployed versions. Export a file to move it.
        </p>
        {storageMessage && <p className="small warn-text">{storageMessage}</p>}
        <button type="button" className="btn small" onClick={download}>
          Export progress (JSON)
        </button>
        <fieldset className="small">
          <legend>Import</legend>
          <label className="check">
            <input type="radio" name="imp" checked={mode === 'merge'} onChange={() => setMode('merge')} /> Merge: keep what is here, add runs and passed checks from the file
          </label>
          <label className="check">
            <input type="radio" name="imp" checked={mode === 'replace'} onChange={() => setMode('replace')} /> Replace everything here with the file
          </label>
          <input
            ref={fileRef}
            type="file"
            accept="application/json,.json"
            aria-label="Choose a progress file to import"
            onChange={async (e) => {
              const f = e.target.files?.[0];
              if (!f) return;
              const res = importData(await f.text(), mode);
              notify(res.ok ? 'success' : 'error', res.message);
              if (fileRef.current) fileRef.current.value = '';
            }}
          />
        </fieldset>
      </section>
      <section>
        <h3>Display</h3>
        <label className="field">
          <span className="field-label">Reduced motion</span>
          <select value={prefs.reducedMotion} onChange={(e) => setPrefs({ reducedMotion: e.target.value as 'system' | 'on' | 'off' })}>
            <option value="system">Follow system setting</option>
            <option value="on">On (no rotor animation)</option>
            <option value="off">Off</option>
          </select>
        </label>
      </section>
      <section>
        <h3>Reset course</h3>
        <p className="small">Deletes all progress, answers and saved runs in this browser. Consider exporting first.</p>
        <label className="field small">
          <span className="field-label">Type RESET to confirm</span>
          <input value={confirmReset} onChange={(e) => setConfirmReset(e.target.value)} />
        </label>
        <button
          type="button"
          className="btn small danger"
          disabled={confirmReset !== 'RESET'}
          onClick={() => {
            resetCourse();
            setConfirmReset('');
            onClose();
            navigate('/lesson/m0');
          }}
        >
          Reset course
        </button>
      </section>
      <button type="button" className="btn small ghost" onClick={onClose}>
        Close menu
      </button>
    </div>
  );
}
